import { z } from 'zod';

import {
  Client as ClientType,
  Company as CompanyType,
  URLValidationRegex,
} from 'src/constants';
import { EmbedErrors } from 'src/constants/errorConsts/errorCodesConsts';
import { InitialConnectAppFormValues } from 'src/legacy/components/ExtensionPage/ConnectManualApp';
import {
  CustomFieldOption,
  isMultiSelectCustomField,
} from 'src/store/clients/types';
import { ExtensionItem, ExtensionItemFields } from 'src/store/dashboard/types';
import UrlUtils from 'src/utils/UrlUtils';

/**
 * Resolves a custom field value, handling multiSelect fields by converting option IDs to labels
 */
const resolveCustomFieldValue = (
  rawValue: unknown,
  customFields?: Record<string, CustomFieldOption>,
  fieldKey?: string,
): string => {
  if (!rawValue) {
    return '';
  }

  // If we have custom field definitions and the field key exists, use the custom field logic
  if (customFields && fieldKey && customFields[fieldKey]) {
    const customField = customFields[fieldKey];
    // Check if this is a multiSelect field and resolve option IDs to labels
    if (isMultiSelectCustomField(customField)) {
      // Handle both single value and array of values
      const optionIds = Array.isArray(rawValue) ? rawValue : [rawValue];
      const labels = optionIds
        .map((optionId) => {
          const option = customField.options?.find(
            (opt) => opt.id === optionId,
          );
          return option?.label;
        })
        .filter((label): label is string => !!label);

      return labels.join(',');
    }
  }

  // Fallback to raw value if no custom field definition or not a multiSelect field
  return typeof rawValue === 'string' ? rawValue : '';
};

export const applyQueryParams = (
  url: string | URL,
  queryParams: Record<string, string>[],
  currentClient?: ClientType,
  currentCompany?: CompanyType,
  clientCustomFields?: Record<string, CustomFieldOption>,
  companyCustomFields?: Record<string, CustomFieldOption>,
): string => {
  if (!queryParams?.length) {
    return url.toString();
  }

  try {
    const urlObj = new URL(url.toString());

    queryParams.forEach(({ key, value }) => {
      let resolvedValue: string = '';
      const isToken =
        value.startsWith('client.') || value.startsWith('company.');

      if (value.startsWith('client.') && currentClient) {
        const [, clientKey] = value.split('.');
        if (clientKey === 'id') {
          if (currentClient?.[clientKey]) {
            resolvedValue = currentClient?.[clientKey];
          }
        } else if (
          clientKey === 'email' ||
          clientKey === 'givenName' ||
          clientKey === 'familyName'
        ) {
          if (currentClient?.fields?.[clientKey]) {
            resolvedValue = currentClient?.fields?.[clientKey];
          }
        } else if (z.string().uuid().safeParse(clientKey.trim()).success) {
          const rawValue = currentClient?.fields?.customFields?.[clientKey];
          resolvedValue = resolveCustomFieldValue(
            rawValue,
            clientCustomFields,
            clientKey,
          );
        }
      }

      if (value.startsWith('company.') && currentCompany) {
        const [, companyKey] = value.split('.');
        if (currentCompany && companyKey === 'id') {
          resolvedValue = currentCompany?.[companyKey] ?? '';
        } else if (companyKey === 'name') {
          resolvedValue = currentCompany?.fields?.[companyKey] ?? '';
        } else if (z.string().uuid().safeParse(companyKey.trim()).success) {
          const rawValue = currentCompany?.fields?.customFields?.[companyKey];
          resolvedValue = resolveCustomFieldValue(
            rawValue,
            companyCustomFields,
            companyKey,
          );
        }
      }

      if (!isToken) {
        resolvedValue = value;
      }

      if (resolvedValue) {
        urlObj.searchParams.set(key, resolvedValue);
      }
    });

    return urlObj.toString();
  } catch (ex) {
    // If URL parsing fails, return the original URL
    return url.toString();
  }
};

const autoSizeElementTypes = ['div', 'iframe'];

/**
 * Checks if a URL string is a localhost URL (http://localhost:port or http://127.0.0.1:port)
 */
const isLocalhostUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname === 'localhost' ||
      urlObj.hostname === '[::1]' ||
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(
        urlObj.hostname,
      )
    );
  } catch {
    return false;
  }
};

/**
 * Validates if an embed URL is allowed.
 * - Standard URLs must match URLValidationRegex
 * - Localhost URLs are only allowed when the webapp is running on localhost,
 *   or when explicitly permitted (e.g., dev-mode)
 */
const isValidEmbedUrl = (url: string, allowLocalhost = false): boolean => {
  // Check if it's a localhost URL
  if (isLocalhostUrl(url)) {
    // Allow localhost if explicitly permitted (e.g., dev-mode) or webapp is on localhost
    return allowLocalhost || UrlUtils.isLocalhost();
  }

  // For non-localhost URLs, use the standard validation regex
  const urlRegex = new RegExp(URLValidationRegex);
  return urlRegex.test(url);
};

// allow-same-origin and allow-scripts intentionally coexist: removing allow-same-origin breaks
// localStorage/cookies/fetch on same-site embeds. MDN warns that same-origin iframes served from
// the same origin as the host can call frameElement.removeAttribute('sandbox') to escape — this
// is accepted because Assembly marketplace apps are always hosted on third-party origins in
// production. Do not add allow-same-origin to untrusted, same-origin embeds.
export const APP_EMBED_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-downloads allow-top-navigation-by-user-activation allow-top-navigation-to-custom-protocols';

export const APP_EMBED_ALLOW = 'clipboard-write';

export const createIFrame = (embedURL: string) =>
  `<iframe src="${embedURL}"></iframe>`;

interface CreateEmbedBodyInput {
  embedCode: string;
  autoSize: boolean;
  customAppParams?: {
    // query params to add to the embed url
    companyId: string;
    clientId?: string; // client id is optional for company channels when viewing as internal user
  };
  queryParams?: Record<string, string>[];
  currentClient?: ClientType;
  currentCompany?: CompanyType;
  clientCustomFields?: Record<string, CustomFieldOption>;
  companyCustomFields?: Record<string, CustomFieldOption>;
  /** Allow localhost URLs even when webapp is not on localhost (e.g., dev-mode) */
  allowLocalhost?: boolean;
}

export const createEmbedBody = ({
  embedCode,
  autoSize,
  customAppParams,
  queryParams,
  currentClient,
  currentCompany,
  clientCustomFields,
  companyCustomFields,
  allowLocalhost,
}: CreateEmbedBodyInput) => {
  let embedSnippet = embedCode;
  if (isValidEmbedUrl(embedCode, allowLocalhost)) {
    try {
      const url = new URL(embedCode);
      // when customAppParams is provided append the params to the url
      if (customAppParams) {
        Object.entries(customAppParams).forEach(([key, value]) => {
          url.searchParams.set(key, value ?? '');
        });
      }

      // get full url and create Iframe snippet
      embedSnippet = createIFrame(url.toString());
    } catch (ex) {
      return EmbedErrors.errorRenderExtension;
    }
  }

  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(embedSnippet, 'text/html');
  const iframe = htmlDoc.body.querySelector('iframe');
  let queryParamsUrl: InstanceType<typeof URL> | undefined;
  try {
    queryParamsUrl = new URL(iframe?.src ?? '');
  } catch (ex) {
    // nothing
  }

  if (queryParams && queryParamsUrl) {
    const updatedUrl = applyQueryParams(
      queryParamsUrl,
      queryParams,
      currentClient,
      currentCompany,
      clientCustomFields,
      companyCustomFields,
    );

    if (iframe) {
      iframe.setAttribute('src', updatedUrl);
    }
  }

  if (iframe) {
    // Assembly enforces APP_EMBED_SANDBOX as a floor policy on all embeds regardless of any
    // sandbox attribute already present in the customer-supplied embed code. This intentionally
    // widens caller-specified sandboxes to ensure consistent platform behaviour (e.g. allow-downloads).
    iframe.setAttribute('sandbox', APP_EMBED_SANDBOX);

    // Merge allow tokens: always include clipboard-write; add local-network-access when allowLocalhost
    const allowTokens = new Set(APP_EMBED_ALLOW.split(' '));
    if (allowLocalhost) {
      allowTokens.add('local-network-access');
    }
    iframe.setAttribute('allow', Array.from(allowTokens).join(' '));

    iframe.setAttribute('referrerpolicy', 'no-referrer');
  }

  // override iframes that have `no` scrolling behavior
  // the explicit check for scrolling=no is recommended since this property
  // is deprecated and we dont want to set the value if it does not need to be
  Array.from(htmlDoc.body.children).forEach((elem) => {
    const elemTagName = elem.tagName.toLowerCase();
    if (elemTagName === 'iframe') {
      const iframeScrollingAttributeValue = elem.getAttribute('scrolling');
      if (iframeScrollingAttributeValue === 'no') {
        (elem as HTMLIFrameElement).scrolling = 'auto';
      }
    }
  });

  if (!autoSize) {
    return htmlDoc.body.innerHTML;
  }

  let resizeHeight = '100%';
  const resizeAbleElements = Array.from(htmlDoc.body.children).filter((elem) =>
    autoSizeElementTypes.includes(elem.tagName.toLowerCase()),
  );
  if (resizeAbleElements.length > 1) {
    // If there are more than one resize-able element, we'll set the height to
    // 50%, to show multiple elements on the page
    resizeHeight = '50%';
  }
  Array.from(htmlDoc.body.children).forEach((elem) => {
    const elemTagName = elem.tagName.toLowerCase();
    if (!autoSizeElementTypes.includes(elemTagName)) {
      return;
    }
    //
    if (elemTagName === 'div') {
      (elem as HTMLDivElement).style.height = resizeHeight;
      (elem as HTMLDivElement).style.width = '100%';
    }
    if (elemTagName === 'iframe') {
      (elem as HTMLIFrameElement).height = resizeHeight;
      (elem as HTMLIFrameElement).width = '100%';
    }
  });

  return htmlDoc.body.innerHTML;
};

/**
 * Method to format the created channel using useChannelForm form
 * @param formData { membersType: is the selected channel of company or client, membersId: list of client ids}
 * @returns
 */
export const formatExtensionChannelMembers = (
  formData: InitialConnectAppFormValues,
) => {
  const extensionItemFields: Partial<ExtensionItemFields> = {};
  const { memberIDs, membersType, companyID } = formData.fields;
  if (!memberIDs) return extensionItemFields;

  const clientMemberIDs: Record<string, boolean> = {};
  if (membersType === 'company') {
    // For company channels, we only need the companyID
    extensionItemFields.companyID = companyID;
  } else {
    // For individual and group channels, we need the member IDs
    memberIDs.forEach((clientID) => {
      clientMemberIDs[clientID] = true;
    });
    extensionItemFields.memberIds = clientMemberIDs;
  }
  return extensionItemFields;
};

/**
 * Method to check is the newly created channel already existed or not
 * @param dashboardItems list of all dashboard items i.e. app channels
 * @param item new selected channel that we are checking for
 * @param enableManyCompanies
 * @returns
 */
export const findExistingDashboardItem = (
  dashboardItems: ExtensionItem[],
  item: Partial<ExtensionItem>,
) => {
  const dashboardMembersMap = item.fields?.memberIds || {};
  const newDashboardMemberIds = Object.keys(dashboardMembersMap);
  return dashboardItems.find((extensionChannelItem) => {
    // check extension configId matches
    if (
      extensionChannelItem.fields.extensionConfigId !==
      item.fields?.extensionConfigId
    ) {
      return false;
    }

    // TODO(Neil:EnableManyCompanies) this if is the only code path we should have
    // once we've backfilled all the embeds to have membershipType set. Going forward
    // this value should always be present
    if (extensionChannelItem.membershipType) {
      // if membershipType is set on the channel use this directly to
      // see if there an existing channel with the same membershipType
      // and company/client composition
      const isSameMembershipType =
        extensionChannelItem.membershipType === item.membershipType;
      const isSameCompanyID =
        extensionChannelItem.fields.companyID === item.fields?.companyID;
      if (extensionChannelItem.membershipType === 'company') {
        return isSameMembershipType && isSameCompanyID;
      } else if (isSameCompanyID) {
        const existingDashboardMemberIds = Object.keys(
          extensionChannelItem.fields.memberIds || {},
        );
        return (
          isSameMembershipType &&
          isSameCompanyID &&
          existingDashboardMemberIds.length === newDashboardMemberIds.length &&
          existingDashboardMemberIds.every(
            (dashboardMemberId) => item.fields?.memberIds?.[dashboardMemberId],
          )
        );
      }
    }

    // if enableManyCompanies is true, we need to check if the new extension channel has a companyID matches with an existing channel with companyID we return that existing channel otherwise skip
    const extItemCompanyID = extensionChannelItem.fields.companyID;
    if (extItemCompanyID) {
      // if the new extension channel has a companyID matches with an existing channel with companyID we return that existing channel otherwise skip
      return extItemCompanyID === item.fields?.companyID;
    }
    // if dashboard extension matches item and channelMembers in current
    const existingDashboardMemberIds = Object.keys(
      extensionChannelItem.fields.memberIds || {},
    );
    return (
      // check channel members in extension match item members
      existingDashboardMemberIds.length === newDashboardMemberIds.length &&
      existingDashboardMemberIds.every(
        (dashboardMemberId) => dashboardMembersMap[dashboardMemberId],
      )
    );
  });
};
